import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';

const Topbar = ({ toggleSidebar }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const dropdownRef = useRef(null);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  // Handle responsive display
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  return (
    <nav style={styles.topbar}>
      <div style={styles.topbarLeft}>
        <button style={styles.menuButton} onClick={toggleSidebar}>
          <i className="fas fa-bars"></i>
        </button>
        <div style={styles.logo}>
          <span style={styles.logoText}>Smart Learning Management System</span>
        </div>
      </div>

      <div style={styles.topbarRight}>
        {/* AI-Powered Badge */}
        <div style={styles.badge}>
          <i className="fas fa-sparkles" style={styles.badgeIcon}></i>
          <span style={styles.badgeText}>AI-Powered</span>
        </div>

        {/* Company Badge - Hidden on mobile */}
        {!isMobile && (
          <div style={styles.companyBadge}>
            <div style={styles.companyLogo}>
              <span style={styles.companyLogoText}>C</span>
            </div>
            <span style={styles.companyBadgeText}>Cognify Analytics</span>
          </div>
        )}

        <div style={styles.userMenuContainer} ref={dropdownRef}>
          <div 
            style={styles.userAvatar} 
            onClick={toggleDropdown}
            title="User menu"
          >
            {user?.username ? user.username.charAt(0).toUpperCase() : user?.email ? user.email.charAt(0).toUpperCase() : 'U'}
          </div>

          {isDropdownOpen && (
            <div style={styles.dropdownMenu}>
              <div style={styles.dropdownHeader}>
                <div style={styles.dropdownAvatar}>
                  {user?.username ? user.username.charAt(0).toUpperCase() : user?.email ? user.email.charAt(0).toUpperCase() : 'U'}
                </div>
                <div style={styles.dropdownUserInfo}>
                  <div style={styles.dropdownUserName}>
                    {user?.username || 'User'}
                  </div>
                  <div style={styles.dropdownUserEmail}>
                    {user?.email || 'No email'}
                  </div>
                </div>
              </div>

              <div style={styles.dropdownDivider}></div>

              <div style={styles.dropdownBody}>
                <div style={styles.dropdownItem}>
                  <i className="fas fa-user-circle" style={styles.dropdownItemIcon}></i>
                  <span style={styles.dropdownItemText}>
                    Role: {user?.role ? user.role.charAt(0).toUpperCase() + user.role.slice(1) : 'Student'}
                  </span>
                </div>
              </div>

              <div style={styles.dropdownDivider}></div>

              <div style={styles.dropdownFooter}>
                <button 
                  style={styles.signOutButton} 
                  onClick={handleLogout}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = styles.signOutButtonHover.backgroundColor;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = styles.signOutButton.backgroundColor;
                  }}
                >
                  <i className="fas fa-sign-out-alt" style={styles.signOutIcon}></i>
                  <span>Sign Out</span>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </nav>
  );
};

const styles = {
  topbar: {
    backgroundColor: '#ffffff',
    borderBottom: '1px solid #e5e7eb',
    height: '70px',
    padding: '0 24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    fontFamily: '"Inter", sans-serif',
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 1001, 
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
  },
  topbarLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '20px',
  },
  menuButton: {
    background: 'none',
    border: 'none',
    color: '#374151',
    fontSize: '18px',
    cursor: 'pointer',
    padding: '8px',
    borderRadius: '6px',
    transition: 'all 0.2s ease',
  },
  logo: {
    display: 'flex',
    alignItems: 'center',
  },
  logoText: {
    fontSize: '20px',
    fontWeight: '700',
    color: '#111827',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
  },
  topbarRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  badge: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '6px 12px',
    borderRadius: '20px',
    background: 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)',
    border: '1px solid rgba(59, 130, 246, 0.4)',
    boxShadow: '0 2px 8px rgba(59, 130, 246, 0.15)',
  },
  badgeIcon: {
    fontSize: '14px',
    color: '#2563eb',
    filter: 'drop-shadow(0 1px 2px rgba(37, 99, 235, 0.3))',
  },
  badgeText: {
    fontSize: '12px',
    fontWeight: '600',
    background: 'linear-gradient(135deg, #1e40af 0%, #2563eb 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
  },
  companyBadge: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '6px 12px',
    borderRadius: '20px',
    background: 'linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)',
    border: '1px solid rgba(16, 185, 129, 0.4)',
    boxShadow: '0 2px 8px rgba(16, 185, 129, 0.15)',
  },
  companyLogo: {
    width: '16px',
    height: '16px',
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #10b981 0%, #14b8a6 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 2px 6px rgba(16, 185, 129, 0.3)',
  },
  companyLogoText: {
    fontSize: '10px',
    fontWeight: '700',
    color: '#ffffff',
    textShadow: '0 1px 2px rgba(0, 0, 0, 0.2)',
  },
  companyBadgeText: {
    fontSize: '12px',
    fontWeight: '600',
    background: 'linear-gradient(135deg, #047857 0%, #10b981 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
  },
  userMenuContainer: {
    position: 'relative',
  },
  userAvatar: {
    width: '44px',
    height: '44px',
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'white',
    fontWeight: '600',
    fontSize: '16px',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    border: '2px solid rgba(255, 255, 255, 0.3)',
    boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4), inset 0 1px 2px rgba(255, 255, 255, 0.2)',
    textShadow: '0 1px 3px rgba(0, 0, 0, 0.3)',
  },
  dropdownMenu: {
    position: 'absolute',
    top: '55px',
    right: '0',
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)',
    minWidth: '280px',
    zIndex: 1002,
    overflow: 'hidden',
    animation: 'slideDown 0.2s ease-out',
  },
  dropdownHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '16px',
    backgroundColor: '#f9fafb',
  },
  dropdownAvatar: {
    width: '48px',
    height: '48px',
    borderRadius: '50%',
    backgroundColor: '#4f46e5',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'white',
    fontWeight: '600',
    fontSize: '18px',
    flexShrink: 0,
  },
  dropdownUserInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    overflow: 'hidden',
  },
  dropdownUserName: {
    fontSize: '15px',
    fontWeight: '600',
    color: '#111827',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  dropdownUserEmail: {
    fontSize: '13px',
    color: '#6b7280',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  dropdownDivider: {
    height: '1px',
    backgroundColor: '#e5e7eb',
  },
  dropdownBody: {
    padding: '8px',
  },
  dropdownItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '10px 12px',
    borderRadius: '6px',
    cursor: 'default',
  },
  dropdownItemIcon: {
    fontSize: '16px',
    color: '#6b7280',
    width: '20px',
    textAlign: 'center',
  },
  dropdownItemText: {
    fontSize: '14px',
    color: '#374151',
  },
  dropdownFooter: {
    padding: '8px',
  },
  signOutButton: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    padding: '10px 16px',
    backgroundColor: '#fee2e2',
    color: '#dc2626',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    fontFamily: '"Inter", sans-serif',
  },
  signOutButtonHover: {
    backgroundColor: '#fecaca',
  },
  signOutIcon: {
    fontSize: '14px',
  },
};

export default Topbar;